import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Search, MapPin, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import apiService from '@/services/api';

// Icon mapping for visual interest
const ICON_EMOJI = {
  '6': '🧹',
  '14': '🪵',
  '15': '🏗️',
  '16': '🌿',
  '17': '⚡',
  '18': '🎨',
  '19': '🚿',
  '20': '🔒',
  '21': '🔧',
  '22': '🛠️',
  '23': '🏠',
  '24': '📋',
  '25': '♻️',
  '26': '🪟'
};

// Metadata lookup for all 14 service categories
const CATEGORY_META: Record<string, { image: string; subtitle: string; tags: string[]; description: string }> = {
  '6': {
    image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=500&h=500&fit=crop',
    subtitle: 'Residential & Commercial',
    tags: ['END-OF-TENANCY', 'DEEP-CLEAN', 'AIRBNB'],
    description: 'Immaculate standards for your home or estate. From routine upkeep to specialized deep-cleans.'
  },
  '14': {
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=500&h=500&fit=crop',
    subtitle: 'Custom Woodwork',
    tags: ['JOINERY', 'CUSTOM', 'INSTALLATIONS'],
    description: 'Expert carpentry and custom joinery for structural and decorative projects.'
  },
  '15': {
    image: 'https://images.unsplash.com/photo-1581576228313-1a0be7fa3398?w=500&h=500&fit=crop',
    subtitle: 'Professional Engineering',
    tags: ['STRUCTURAL', 'ENGINEERING', 'MAJOR-WORKS'],
    description: 'Specialist structural assessment and major construction projects.'
  },
  '16': {
    image: 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=500&h=500&fit=crop',
    subtitle: 'Garden Design',
    tags: ['LANDSCAPING', 'GARDENS', 'OUTDOOR'],
    description: 'Professional landscaping and garden design for beautiful outdoor spaces.'
  },
  '17': {
    image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=500&h=500&fit=crop',
    subtitle: 'Smart & Safe',
    tags: ['EICR', 'INSTALLATION', 'SMART-HOME'],
    description: 'Certified electrical work from safety inspections to smart home systems.'
  },
  '18': {
    image: 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=500&h=500&fit=crop',
    subtitle: 'Interior Design',
    tags: ['PAINTING', 'DECORATING', 'FINISHING'],
    description: 'Professional interior finishing and decorative services.'
  },
  '19': {
    image: 'https://images.unsplash.com/photo-1607410632539-29a1a9b7cdf8?w=500&h=500&fit=crop',
    subtitle: 'Renovation',
    tags: ['KITCHENS', 'BATHROOMS', 'REFIT'],
    description: 'Complete kitchen and bathroom installations and renovations.'
  },
  '20': {
    image: 'https://images.unsplash.com/photo-1565183197515-bcd58e90f951?w=500&h=500&fit=crop',
    subtitle: 'Security Systems',
    tags: ['LOCKS', 'CCTV', 'ACCESS-CONTROL'],
    description: 'Professional security and access control system installation.'
  },
  '21': {
    image: 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=500&h=500&fit=crop',
    subtitle: 'Heating & Gas',
    tags: ['BOILER', 'PLUMBING', 'GAS-SAFE'],
    description: 'Expert plumbing and heating system installations and repairs.'
  },
  '22': {
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=500&h=500&fit=crop',
    subtitle: 'General Maintenance',
    tags: ['HANDYMAN', 'REPAIRS', 'MAINTENANCE'],
    description: 'Versatile maintenance and handyman services for your property.'
  },
  '23': {
    image: 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=500&h=500&fit=crop',
    subtitle: 'Roof Experts',
    tags: ['ROOFING', 'INSPECTION', 'REPAIR'],
    description: 'Professional roof inspection, maintenance, and repair services.'
  },
  '24': {
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500&h=500&fit=crop',
    subtitle: 'Compliance Services',
    tags: ['CERTIFICATION', 'COMPLIANCE', 'LEGAL'],
    description: 'Specialist compliance services including certifications and legal requirements.'
  },
  '25': {
    image: 'https://images.unsplash.com/photo-1578789358786-05dcc1a9ab0d?w=500&h=500&fit=crop',
    subtitle: 'Waste Management',
    tags: ['CLEARANCE', 'RECYCLING', 'DISPOSAL'],
    description: 'Responsible waste and clearance services with environmental care.'
  },
  '26': {
    image: 'https://images.unsplash.com/photo-1565636192335-14a305d2b126?w=500&h=500&fit=crop',
    subtitle: 'Windows & Doors',
    tags: ['GLAZING', 'INSTALLATION', 'FRAMES'],
    description: 'Professional windows, doors, and glazing installation and repair.'
  }
};

interface CategoryData {
  id: string;
  name: string;
  description?: string;
  service_count?: number;
  slug?: string;
  subtitle?: string;
  image?: string;
  tags?: string[];
}

interface Service {
  id: string;
  name: string;
  description?: string;
  category_id: number | string;
}

const ServicesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch categories and services from public endpoints
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories with nested services
        const response = await apiService.get('/public/service-categories-with-services');
        if (response.success && Array.isArray(response.data)) {
          const enrichedCategories = response.data.map((cat: any) => {
            const meta = CATEGORY_META[String(cat.id)] || {};
            return {
              id: cat.slug || String(cat.id),
              categoryId: cat.id,
              name: cat.name,
              description: meta.description || cat.description || '',
              service_count: cat.services ? cat.services.length : 0,
              slug: cat.slug,
              subtitle: meta.subtitle || cat.name,
              image: meta.image || 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=500&h=500&fit=crop',
              tags: meta.tags || [cat.name.toUpperCase()]
            };
          });
          setCategories(enrichedCategories);

          // Extract all services from the nested structure
          const services: Service[] = [];
          response.data.forEach((cat: any) => {
            if (cat.services && Array.isArray(cat.services)) {
              cat.services.forEach((svc: any) => {
                services.push({
                  id: svc.id || svc.name,
                  name: svc.name,
                  description: svc.description,
                  category_id: cat.id
                });
              });
            }
          });
          setAllServices(services);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get services for a category
  const getServicesForCategory = (categoryId: string | number) => {
    return allServices.filter(svc => String(svc.category_id) === String(categoryId));
  };

  // Generate category filter buttons from actual categories
  const categoryFilters = ['ALL', ...categories.map(c => c.name.toUpperCase())];

  // Filter categories based on search and selected category
  const filteredCategories = categories.filter(cat => {
    const services = getServicesForCategory(cat.categoryId || cat.id);
    const hasServices = services.length > 0;
    const matchesSearch = cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (cat.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'ALL' ||
                           cat.name.toUpperCase() === selectedCategory;
    return hasServices && matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      <Helmet>
        <title>Our Services | Virewire - Professional Property Management</title>
        <meta name="description" content="Comprehensive estate management and professional property services. Cleaning, maintenance, plumbing, handyman, decorating, and logistics services." />
      </Helmet>

      {/* Breadcrumb */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-gray-900">HOME</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">SERVICES</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bg-teal-700 text-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif font-light mb-4">Our Services</h1>
          <p className="text-lg text-teal-50 mb-2">
            Comprehensive estate management and professional property services, meticulously curated and expertly delivered.
          </p>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="What service do you need today?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 py-3 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          
          {/* Category Filter Tabs */}
          <div className="flex flex-wrap gap-3">
            {categoryFilters.map(filter => (
              <button
                key={filter}
                onClick={() => setSelectedCategory(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === filter
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid - Organized by Category */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCategories.map(category => {
              const categoryServices = getServicesForCategory(category.categoryId || category.id);
              const categoryIcon = ICON_EMOJI[String(category.categoryId || category.id)] || '✨';
              const gradients = [
                'from-teal-500 to-teal-600',
                'from-blue-500 to-blue-600',
                'from-emerald-500 to-emerald-600',
                'from-cyan-500 to-cyan-600',
                'from-indigo-500 to-indigo-600',
                'from-teal-600 to-cyan-600',
                'from-emerald-600 to-teal-600',
                'from-blue-600 to-indigo-600',
                'from-cyan-600 to-blue-600',
                'from-teal-500 to-emerald-600',
                'from-indigo-600 to-teal-600',
                'from-emerald-500 to-cyan-600',
                'from-blue-500 to-cyan-600',
                'from-teal-600 to-indigo-600'
              ];
              const gradient = gradients[(category.categoryId || 6) % gradients.length];

              return (
                <div
                  key={category.id}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 flex flex-col"
                >
                  {/* Category Header - Attractive */}
                  <div className={`bg-gradient-to-br ${gradient} overflow-hidden relative p-6 text-white`}>
                    {/* Decorative background elements */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mt-8"></div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-4 -mb-4"></div>

                    {/* Icon and content */}
                    <div className="relative z-10">
                      <div className="text-4xl mb-3">{categoryIcon}</div>
                      <h2 className="text-lg font-bold leading-tight mb-1">{category.name}</h2>
                      <p className="text-xs font-medium text-white/80">{category.subtitle}</p>

                      {/* Service count badge */}
                      <div className="mt-3 inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                        <span className="text-xs font-semibold">{categoryServices.length} services</span>
                      </div>
                    </div>
                  </div>

                  {/* Category Description & Services */}
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-xs text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                      {category.description}
                    </p>

                    {/* Services List - Attractive */}
                    {categoryServices.length > 0 ? (
                      <div className="mb-4 flex-1">
                        <div className="space-y-2.5">
                          {categoryServices.slice(0, 4).map(svc => (
                            <div
                              key={svc.id}
                              className="flex items-start gap-2 p-2.5 rounded-lg hover:bg-gradient-to-r hover:from-teal-50 hover:to-transparent transition-all duration-200 cursor-pointer group"
                            >
                              <span className="text-teal-500 font-bold mt-0.5 group-hover:translate-x-1 transition-transform">•</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-teal-700 transition-colors">{svc.name}</p>
                                {svc.description && (
                                  <p className="text-xs text-gray-500 line-clamp-1">{svc.description}</p>
                                )}
                              </div>
                            </div>
                          ))}
                          {categoryServices.length > 4 && (
                            <div className="pt-1 pl-2">
                              <p className="text-xs text-teal-600 font-semibold hover:text-teal-700">
                                +{categoryServices.length - 4} more services →
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic mb-4">Services coming soon</p>
                    )}

                    {/* Action Button - Bottom */}
                    <div className="pt-3 border-t border-gray-200">
                      <Button
                        asChild
                        className="w-full h-9 text-xs font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-all"
                      >
                        <Link to={`/services/category/${category.categoryId || category.id}`} className="flex items-center justify-center gap-1">
                          <span>Details</span>
                          <ArrowRight className="w-3 h-3" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredCategories.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-600 text-lg font-medium">No services match your search.</p>
              <p className="text-gray-500 text-sm mt-2">Try adjusting your filters or search terms</p>
            </div>
          )}
        </div>
      </section>

      {/* Quality Assurance Section */}
      <section className="bg-teal-50 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <MapPin className="h-8 w-8 text-teal-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-lg text-gray-900 font-medium mb-3">
                Every service is managed by the Virewire platform.
              </p>
              <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                <span className="font-medium">Quality Guaranteed</span>
                <span className="font-medium">Vetted Professionals</span>
                <span className="font-medium">24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="bg-teal-700 text-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-light mb-4">
            Need a bespoke solution for your portfolio?
          </h2>
          <p className="text-lg text-teal-50 mb-8 max-w-2xl mx-auto">
            Whether you manage a single home or a multi-national property portfolio, our team can design a management package tailored specifically to your operational needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-white text-teal-700 hover:bg-gray-100 font-medium"
            >
              <Link to="/contact">TALK TO A CONSULTANT</Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-white text-white hover:bg-teal-600"
            >
              <Link to="/service">EXPLORE ENTERPRISE</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
